<template>
  <b-form-row>
    <b-form-group
      :label="$t('inputs.customer.salutation')"
      class="col-4 col-sm-2 col-lg-2"
    >
      <b-form-select
        v-e2e:customerContactSalutation
        v-model="model.salutation"
        :options="salutations"
        @input="modelChanged"
        :disabled="readonly"
      />
    </b-form-group>
    <validating-group
      :label="$t('inputs.customer.first-name')"
      :validation-context="validationContext"
      :validation-path="`${validationPath}.first_name`"
      :rules="validationRules"
      v-slot="{ clearErrors, state }"
      class="col-8 col-sm-5 col-lg"
    >
      <b-form-input
        v-e2e:customerContactFirstName
        v-model="model.first_name"
        @input="
          modelChanged();
          clearErrors();
        "
        :readonly="readonly"
        :state="state"
      />
    </validating-group>
    <validating-group
      :label="$t('inputs.customer.last-name')"
      :validation-context="validationContext"
      :validation-path="`${validationPath}.last_name`"
      :rules="validationRules"
      v-slot="{ clearErrors, state }"
      class="col-12 col-sm-5 col-lg"
    >
      <b-form-input
        v-e2e:customerContactLastName
        v-model="model.last_name"
        @input="
          modelChanged();
          clearErrors();
        "
        :readonly="readonly"
        :state="state"
      />
    </validating-group>
    <div v-if="!extraFields" class="w-100"></div>
    <b-form-group
      :label="$t('inputs.customer.eid')"
      class="col-12 col-lg"
      v-if="extraFields"
    >
      <b-form-input
        v-e2e:customerContactEid
        v-model="model.eid"
        @input="modelChanged"
        :readonly="readonly"
      />
    </b-form-group>
    <b-form-group
      :label="$t('inputs.customer.company')"
      class="col-12"
      v-if="extraFields"
    >
      <b-form-input
        v-e2e:customerContactCompanyName
        v-model="model.company_name"
        @input="modelChanged"
        :readonly="readonly"
      />
    </b-form-group>
    <validating-group
      :label="$t('email')"
      :validation-context="validationContext"
      :validation-path="`${validationPath}.email`"
      :rules="{ email: true }"
      v-slot="{ clearErrors, state }"
      class="col-sm-4"
    >
      <b-form-input
        v-e2e:customerContactEmail
        v-model="model.email"
        @input="
          modelChanged();
          clearErrors();
        "
        :readonly="readonly"
        :state="state"
      />
    </validating-group>
    <validating-group
      :label="$t('email-for-invoices')"
      :validation-context="validationContext"
      :validation-path="`${validationPath}.email_for_invoices`"
      :rules="{ email: true }"
      v-slot="{ clearErrors, state }"
      class="col-sm-4"
    >
      <b-form-input
        v-e2e:customerContactEmailForInvoices
        v-model="model.email_for_invoices"
        @input="
          modelChanged();
          clearErrors();
        "
        :readonly="readonly"
        :state="state"
      />
    </validating-group>
    <validating-group
      :label="$t('inputs.customer.phone')"
      :validation-context="validationContext"
      :validation-path="`${validationPath}.phone`"
      :rules="{ phone: true }"
      v-slot="{ clearErrors, state }"
      class="col-sm-4"
    >
      <b-input-group>
        <template #prepend>
          <b-dropdown variant="outline-primary">
            <template #button-content>
              <span class="dialing-country__selected">
                <country-flag
                  :country="countryPhone.code"
                  class="dialing-country__selected__flag"
                />
                <span class="dialing-country__selected__prefix">
                  ( {{ countryPhone.dialing }} )
                </span>
              </span>
            </template>
            <b-dropdown-item
              @click="selectCountryPhone(country)"
              v-for="country in countriesTier1"
              :key="country.name"
              :value="country.code"
            >
              <div>
                <country-flag :country="country.code" />
                <span class="ml-2">
                  {{ country.name }} ( {{ country.dialing }} )
                </span>
              </div>
            </b-dropdown-item>

            <b-dropdown-divider></b-dropdown-divider>

            <b-dropdown-item
              @click="selectCountryPhone(country)"
              v-for="country in countriesTier2"
              :key="country.name"
              :value="country.code"
            >
              <div>
                <country-flag :country="country.code" />
                <span class="ml-2">
                  {{ country.name }} ( {{ country.dialing }} )
                </span>
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </template>
        <b-form-input
          v-e2e:customerContactPhone
          v-model="modelPhone"
          @input="
            modelChanged();
            clearErrors();
          "
          :readonly="readonly"
          :state="state"
        />
      </b-input-group>
    </validating-group>
    <b-form-group :label="$t('inputs.customer.mobile')" class="col-sm-4">
      <b-input-group>
        <template #prepend>
          <b-dropdown variant="outline-primary">
            <template #button-content>
              <span class="dialing-country__selected">
                <country-flag
                  :country="countryMobilePhone.code"
                  class="dialing-country__selected__flag"
                />
                <span class="dialing-country__selected__prefix">
                  ( {{ countryMobilePhone.dialing }} )
                </span>
              </span>
            </template>
            <b-dropdown-item
              @click="selectCountryMobilePhone(country)"
              v-for="country in countriesTier1"
              :key="country.name"
              :value="country.code"
            >
              <div>
                <country-flag :country="country.code" />
                <span class="ml-2">
                  {{ country.name }} ( {{ country.dialing }} )
                </span>
              </div>
            </b-dropdown-item>

            <b-dropdown-divider></b-dropdown-divider>

            <b-dropdown-item
              @click="selectCountryMobilePhone(country)"
              v-for="country in countriesTier2"
              :key="country.name"
              :value="country.code"
            >
              <div>
                <country-flag :country="country.code" />
                <span class="ml-2">
                  {{ country.name }} ( {{ country.dialing }} )
                </span>
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </template>
        <b-form-input
          v-e2e:customerContactMobilePhone
          v-model="modelMobilePhone"
          @input="modelChanged"
          :readonly="readonly"
        />
      </b-input-group>
    </b-form-group>
  </b-form-row>
</template>

<script>
import axios from 'axios';
import CountryFlag from 'vue-country-flag';

import BaseInput from './mixins/BaseInput';
import ValidatingInput from '../mixins/ValidatingInput';

export default {
  mixins: [BaseInput, ValidatingInput],
  props: {
    extraFields: Boolean,
  },
  components: {
    CountryFlag,
  },
  data() {
    return {
      model: {
        salutation: '',
        first_name: null,
        last_name: null,
        eid: null,
        company_name: null,
        email: null,
        email_for_invoices: null,
        phone: null,
        mobile_phone: null,
      },
      salutations: [],
      countryPhone: {
        name: this.$t('country.germany'),
        code: 'DE',
        dialing: '+49',
      },
      countryMobilePhone: {
        name: this.$t('country.germany'),
        code: 'DE',
        dialing: '+49',
      },
      countries: [
        { name: this.$t('country.germany'), code: 'DE', dialing: '+49' },
        { name: this.$t('country.austria'), code: 'AT', dialing: '+43' },
        { name: this.$t('country.france'), code: 'FR', dialing: '+33' },
        { name: this.$t('country.gb'), code: 'GB', dialing: '+44' },
        { name: this.$t('country.sweden'), code: 'SE', dialing: '+46' },

        { name: this.$t('country.netherlands'), code: 'NL', dialing: '+31' },
        { name: this.$t('country.belgium'), code: 'BE', dialing: '+32' },
        { name: this.$t('country.switzerland'), code: 'CH', dialing: '+41' },
        { name: this.$t('country.denmark'), code: 'DK', dialing: '+45' },
        { name: this.$t('country.poland'), code: 'PL', dialing: '+48' },
        { name: this.$t('country.luxembourg'), code: 'LU', dialing: '+352' },
        { name: this.$t('country.slovenia'), code: 'SI', dialing: '+386' },
        {
          name: this.$t('country.czech-republic'),
          code: 'CZ',
          dialing: '+420',
        },
      ],
    };
  },
  watch: {
    passedValue() {
      this.loadModel();
    },
  },
  async created() {
    const picklistId = 'StandardValueSet.Salutation';
    const { data: picklistInfo } = await axios.get('/data-api/sf-picklist', {
      params: {
        api_names: picklistId,
        values_only: true,
      },
    });

    this.salutations = picklistInfo[picklistId].Metadata.customValue.map(
      (item) => ({
        value: item.valueName,
        text: item.label,
      }),
    );

    this.loadModel();
  },
  computed: {
    modelPhone: {
      get() {
        if (this.model.phone) {
          return this.model.phone.replace(this.countryPhone.dialing, '');
        }
        return null;
      },
      set(modelPhone) {
        if (modelPhone) {
          this.model.phone = `${this.countryPhone.dialing}${modelPhone}`;
        } else {
          this.model.phone = null;
        }
        this.modelChanged();
      },
    },
    modelMobilePhone: {
      get() {
        if (this.model.mobile_phone) {
          return this.model.mobile_phone.replace(
            this.countryMobilePhone.dialing,
            '',
          );
        }
        return null;
      },
      set(modelMobilePhone) {
        if (modelMobilePhone) {
          this.model.mobile_phone = `${this.countryMobilePhone.dialing}${modelMobilePhone}`;
        } else {
          this.model.mobile_phone = null;
        }
        this.modelChanged();
      },
    },
    countriesTier1() {
      return this.countries.slice(0, 5);
    },
    countriesTier2() {
      return this.countries.slice(5, this.countries.length);
    },
  },
  methods: {
    loadModel() {
      if (this.passedValue) {
        let newModel = JSON.parse(JSON.stringify(this.passedValue));

        // Add existing salution if not in the predefined list
        const salutationValues = this.salutations.map((item) => item.value);
        if (
          newModel.salutation !== null &&
          !salutationValues.includes(newModel.salutation)
        ) {
          this.salutations.push({
            value: newModel.salutation,
            text: newModel.salutation,
            disabled: true,
          });
        }

        if (this.model.phone) {
          const dialingCountry = this.countries
            .map((c) => c.dialing)
            .find((dialing) => this.model.phone.startsWith(dialing));
          if (dialingCountry) {
            this.countryPhone =
              this.countries.find((c) => c.dialing === dialingCountry) ||
              this.countries[0];
          }
        }

        if (this.model.mobile_phone) {
          const dialingCountry = this.countries
            .map((c) => c.dialing)
            .find((dialing) => this.model.mobile_phone.startsWith(dialing));
          if (dialingCountry) {
            this.countryMobilePhone =
              this.countries.find((c) => c.dialing === dialingCountry) ||
              this.countries[0];
          }
        }

        this.model = newModel;
      }
    },
    modelChanged() {
      const emitModel = JSON.parse(JSON.stringify(this.model));
      if (!this.extraFields) {
        delete emitModel.eid;
        delete emitModel.company_name;
      }
      ['email', 'email_for_invoices', 'phone', 'mobile_phone'].forEach(
        (prop) => {
          if (emitModel[prop] === '') {
            delete emitModel[prop];
          }
        },
      );
      this.commitOrEmit(emitModel);
    },
    selectCountryPhone(country) {
      this.countryPhone = country;
      this.model.phone = '';
    },
    selectCountryMobilePhone(country) {
      this.countryMobilePhone = country;
      this.model.mobile_phone = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.input-group-prepend {
  .dropdown.b-dropdown.btn-group {
    height: 38px;
  }
  button {
    &:after {
      content: '';
      display: block;
      padding-top: 2px;
    }
    .normal-flag {
      margin-top: -8px;
    }
  }
}

.dropdown-item {
  div {
    span.flag.normal-flag {
      margin: -13px -15px;
    }
  }
}

.dialing-country__selected__prefix {
  position: relative;
  top: -4.5px;
  left: 4.5px;
  padding-right: 4.5px;
}
</style>
